import type { ViewRecordFieldChange } from '@salescore/client-redux'
import {
  assignInnerRowGeometry,
  getVisibleNodeNames,
  type ViewQueryList,
  type ViewQueryRecordNode,
} from '@salescore/core'
import { Array, Equivalence, pipe, Result } from 'effect'

import { getLabeledFieldName } from '../../state/util'
import type { DeepReadonly } from '../types/DeepReadonly'
import { type CreateNewChildRecordLeft, createNewChildRecordPure } from './createNewChildRecord'
import { isNewRecord } from './isNewRecord'
import { replaceChildRecordNode } from './replaceChildRecordNode'
import type { RecordChange } from './updateViewRecords'

export type UpdateChildViewRecordLeft =
  | {
      message: '{{label}}は作成禁止です' | '{{label}}は更新禁止です'
      arguments: { label: string }
    }
  | {
      message: 'ルートノードの更新に失敗しました'
    }
  | CreateNewChildRecordLeft
  | undefined

// eslint-disable-next-line sonarjs/cognitive-complexity
export function updateChildViewRecordPure(
  recordChange: DeepReadonly<RecordChange>,
  queryList: DeepReadonly<Pick<ViewQueryList, 'fields' | 'tree'>>,
  childRecordNode: DeepReadonly<ViewQueryRecordNode> | undefined,
  rootRecordNode: DeepReadonly<ViewQueryRecordNode>,
): Result.Result<
  DeepReadonly<{
    newChange: ViewRecordFieldChange
    newRootNode: ViewQueryRecordNode
  }>,
  UpdateChildViewRecordLeft
> {
  const { rowIndex, innerRowIndex, field, value, label, nodePath } = recordChange

  if (field.write === undefined) {
    return Result.fail({
      message: 'このブロックは書き込み禁止です',
    })
  }

  const property = field.name

  // 指定したinnerRowIndexが存在しないときは、子レコードを新規作成する
  // changesも参照渡しで更新する
  if (childRecordNode === undefined) {
    if (field.meta.creatable === false) {
      return Result.fail({
        message: '{{label}}は作成禁止です',
        arguments: { label: field.meta.label },
      })
    }
    const labelFieldName = getLabeledFieldName(field.name)
    const createResult = createNewChildRecordPure({
      attributes: {
        [property]: value,
        ...(label === undefined ? {} : { [labelFieldName]: label }),
      },
      rootRecordNode,
      nodePath: field.nodePath,
      innerRowIndex,
      height: 1,
      queryList,
      change: {
        [field.write.propertySourceName]: value,
      },
    })
    if (Result.isFailure(createResult)) {
      return Result.fail(createResult.failure)
    }
    const newRootNode = assignInnerRowGeometry(createResult.success.rootNode, getVisibleNodeNames(queryList))
    // ルートレコードを更新
    return Result.succeed({
      newChange: createResult.success.newChange,
      newRootNode,
    })
  }

  // 値が同じならば更新は行わない（更新を行わないことで、再レンダリングを行わないようにする）
  const oldValue = childRecordNode.attributes[property]
  if (oldValue === value) {
    return Result.fail(undefined) // わざわざユーザに通知しない
  }
  if (field.meta.creatable === false && isNewRecord(childRecordNode)) {
    return Result.fail({
      message: '{{label}}は作成禁止です',
      arguments: { label: field.meta.label },
    })
  }
  if (field.meta.updatable === false && !isNewRecord(childRecordNode)) {
    return Result.fail({
      message: '{{label}}は更新禁止です',
      arguments: { label: field.meta.label },
    })
  }

  const newChildRecordNode = {
    ...childRecordNode,
    attributes: {
      ...childRecordNode.attributes,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- TODO: fix
      [property]: value as string,
      ...(label === undefined ? {} : { [getLabeledFieldName(field.name)]: label }),
    },
  }
  if (newChildRecordNode.id === undefined) {
    // ViewQueryRecordNode.id が undefined を許容する型なので一応チェックしているが、実際にはありえないはず
    return Result.fail(undefined)
  } else {
    const nameField = queryList.fields
      .filter((x) => Array.makeEquivalence(Equivalence.String)(x.nodePath, field.nodePath))
      .find((field) => field.meta.fieldMetaType === 'name')
    const newChange = {
      id: newChildRecordNode.id,
      streamName: field.write.streamName,
      fieldChanges: [
        {
          id: newChildRecordNode.id,
          fieldName: field.name,
          propertySourceName: field.write.propertySourceName,
          value,
          valueLabel: label,
          rowIndex,
          innerRowIndex,
        },
      ],
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      recordNameLabel: nameField === undefined ? undefined : (newChildRecordNode.attributes[nameField.name] as string),
      before: {
        [field.write.propertySourceName]: oldValue,
      },
      after: {
        [field.write.propertySourceName]: value,
      },
    }
    return pipe(
      replaceChildRecordNode(rootRecordNode, nodePath, newChildRecordNode),
      Result.match({
        onFailure: () =>
          Result.fail({
            message: 'ルートノードの更新に失敗しました',
          }),
        onSuccess: (newRootNode) =>
          Result.succeed({
            newChange,
            newRootNode,
          }),
      }),
    )
  }
}
